import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import axiosAdmin from '../../../../services/axiosAdmin';
import { displayErrors, formataMoeda } from '../../../../util/diversos';

import './dashboardEstamparia.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const PERIODOS = [
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'mes', label: 'Este mês' },
  { id: 'mesAnterior', label: 'Mês anterior' },
  { id: 'ano', label: 'Este ano' },
];

const SITUACAO_PEDIDO = {
  ABERTO: { label: 'Abertos', icon: 'pi-inbox', classe: 'is-aberto' },
  PENDENTE: { label: 'Pendentes', icon: 'pi-clock', classe: 'is-pendente' },
  FECHADO: { label: 'Fechados', icon: 'pi-check-circle', classe: 'is-fechado' },
  CANCELADO: { label: 'Cancelados', icon: 'pi-times-circle', classe: 'is-cancelado' },
};

const ETAPA_OP = {
  PLANEJADA: 'Planejada',
  CORTANDO: 'Cortando',
  SILKANDO: 'Silkando',
  BORDANDO: 'Bordando',
  SUBLIMANDO: 'Sublimando',
  ARREMATANDO: 'Arrematando',
};

function calcularPeriodo(id) {
  const hoje = moment();
  switch (id) {
    case '7d': return [hoje.clone().subtract(6, 'days'), hoje];
    case '30d': return [hoje.clone().subtract(29, 'days'), hoje];
    case 'mesAnterior': {
      const inicio = hoje.clone().subtract(1, 'month').startOf('month');
      return [inicio, inicio.clone().endOf('month')];
    }
    case 'ano': return [hoje.clone().startOf('year'), hoje];
    default: return [hoje.clone().startOf('month'), hoje];
  }
}

// LocalDate pode vir como array [aaaa, m, d] ou como texto "aaaa-mm-dd"
function paraMoment(data) {
  if (!data) return null;
  if (Array.isArray(data)) return moment([data[0], data[1] - 1, data[2]]);
  return moment(data, 'YYYY-MM-DD');
}

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function moeda(valor) {
  return formataMoeda(Number(valor) || 0);
}

function moedaCurta(valor) {
  const v = Number(valor) || 0;
  if (Math.abs(v) >= 1000000) return `R$ ${(v / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (Math.abs(v) >= 10000) return `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return moeda(v);
}

function numero(valor) {
  return (Number(valor) || 0).toLocaleString('pt-BR');
}

function lerCor(variavel, padrao) {
  try {
    const cor = getComputedStyle(document.documentElement).getPropertyValue(variavel).trim();
    return cor || padrao;
  } catch (e) {
    return padrao;
  }
}

export default function DashboardEstamparia() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user) || {};

  const [periodo, setPeriodo] = useState('mes');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [tema, setTema] = useState(document.documentElement.getAttribute('data-theme'));

  // Acompanha a troca de tema para redesenhar o gráfico com a cor nova
  useEffect(() => {
    const observer = new MutationObserver(() => setTema(document.documentElement.getAttribute('data-theme')));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      try {
        setCarregando(true);
        setErro('');
        const [inicio, fim] = calcularPeriodo(periodo);
        const idUsuario = get(user, 'id', 0);
        const params = `/estampariadashboard?idUsuario=${idUsuario}`
          + `&dataInicial=${inicio.format('DD-MM-YYYY')}&dataFinal=${fim.format('DD-MM-YYYY')}`;
        const { data } = await axiosAdmin.get(params);
        if (ativo) setDados(data);
      } catch (err) {
        if (ativo) {
          setErro('Não foi possível carregar os indicadores.');
          displayErrors(err, dispatch, navigate, '', '');
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => { ativo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo]);

  const [inicioPeriodo, fimPeriodo] = calcularPeriodo(periodo);

  const resumo = useMemo(() => {
    if (!dados) return null;
    const qtdPedidos = Number(dados.pedidosQuantidade) || 0;
    const qtdOrcamentos = Number(dados.orcamentosQuantidade) || 0;
    return {
      ticketMedio: qtdPedidos > 0 ? (Number(dados.pedidosValor) || 0) / qtdPedidos : 0,
      conversao: qtdOrcamentos > 0 ? Math.round((qtdPedidos / qtdOrcamentos) * 100) : null,
    };
  }, [dados]);

  // Série do gráfico: por dia, ou por mês quando o período é longo
  const serie = useMemo(() => {
    const lista = get(dados, 'serieDiaria', []) || [];
    const agruparMes = lista.length > 62;
    const mapa = new Map();
    lista.forEach((item) => {
      const dia = paraMoment(item.data);
      if (!dia) return;
      const chave = agruparMes ? dia.format('YYYY-MM') : dia.format('YYYY-MM-DD');
      const rotulo = agruparMes ? `${MESES[dia.month()]}/${dia.format('YY')}` : dia.format('DD/MM');
      const atual = mapa.get(chave) || { rotulo, valor: 0, pedidos: 0, orcamentos: 0 };
      atual.valor += Number(item.pedidosValor) || 0;
      atual.pedidos += Number(item.pedidosQuantidade) || 0;
      atual.orcamentos += Number(item.orcamentosQuantidade) || 0;
      mapa.set(chave, atual);
    });
    return { agruparMes, pontos: Array.from(mapa.values()) };
  }, [dados]);

  const grafico = useMemo(() => {
    const corBarra = lerCor('--brand-600', '#2d7a71');
    const corTexto = '#6b7a86';
    return {
      data: {
        labels: serie.pontos.map(p => p.rotulo),
        datasets: [{
          label: 'Valor dos pedidos',
          data: serie.pontos.map(p => Math.round(p.valor * 100) / 100),
          backgroundColor: corBarra,
          hoverBackgroundColor: lerCor('--brand-800', corBarra),
          borderRadius: { topLeft: 4, topRight: 4 },
          borderSkipped: 'bottom',
          maxBarThickness: 28,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 250 },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#1d2a33',
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (itens) => (serie.agruparMes ? itens[0].label : `Dia ${itens[0].label}`),
              label: (item) => {
                const ponto = serie.pontos[item.dataIndex];
                return [
                  `Pedidos: ${moeda(ponto.valor)} (${numero(ponto.pedidos)})`,
                  `Orçamentos: ${numero(ponto.orcamentos)}`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: corTexto, maxRotation: 0, autoSkipPadding: 12, font: { size: 11 } },
            border: { display: false },
          },
          y: {
            beginAtZero: true,
            grid: { color: '#eef2f4', drawBorder: false },
            ticks: { color: corTexto, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => moedaCurta(v) },
          },
        },
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serie, tema]);

  const etapas = get(dados, 'ordensPorEtapa', []) || [];
  const maiorEtapa = Math.max(1, ...etapas.map(e => Number(e.quantidade) || 0));
  const vendedores = get(dados, 'vendedores', []) || [];
  const maiorVendedor = Math.max(1, ...vendedores.map(v => Number(v.valor) || 0));
  const totalSituacoes = (get(dados, 'pedidosPorSituacao', []) || []).reduce((s, i) => s + (Number(i.quantidade) || 0), 0);

  // eslint-disable-next-line react/no-unstable-nested-components, react/prop-types
  function ListaPedidos({ titulo, icone, itens, vazio, atraso }) {
    return (
      <section className="dash-card">
        <header className="dash-card-header">
          <h2><i className={`pi ${icone}`} />{titulo}</h2>
          <button type="button" className="dash-link" onClick={() => navigate('/consultaestampariapedido')}>
            Ver pedidos
          </button>
        </header>
        {itens.length === 0 ? (
          <p className="dash-empty">{vazio}</p>
        ) : (
          <ul className="dash-list">
            {itens.map((p) => {
              const previsao = paraMoment(p.dataPrevisaoEntrega);
              const dias = previsao ? moment().startOf('day').diff(previsao, 'days') : 0;
              return (
                <li key={p.id}>
                  <div className="dash-list-main">
                    <strong>{p.nomePedido}</strong>
                    <span>{p.cliente || 'Cliente não informado'}</span>
                  </div>
                  <div className="dash-list-side">
                    <span className={`dash-date ${atraso ? 'is-late' : ''}`}>
                      {previsao ? previsao.format('DD/MM') : '--'}
                      {atraso && dias > 0 ? <em>{dias} {dias === 1 ? 'dia' : 'dias'}</em> : null}
                      {!atraso && previsao ? <em>{dias === 0 ? 'hoje' : DIAS_SEMANA[previsao.day()]}</em> : null}
                    </span>
                    <span className="dash-value">{moeda(p.valor)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    );
  }

  return (
    <div className="dash-estamparia">
      <div className="dash-top">
        <div>
          <span className="section-eyebrow">Visão geral</span>
          <h1>Olá{user.nome ? `, ${user.nome.split(' ')[0]}` : ''}!</h1>
          <p>
            {inicioPeriodo.format('DD/MM/YYYY')} a {fimPeriodo.format('DD/MM/YYYY')}
            {carregando ? <span className="dash-loading"><i className="pi pi-spin pi-spinner" /> atualizando</span> : null}
          </p>
        </div>
        <div className="dash-periods" role="group" aria-label="Período">
          {PERIODOS.map(p => (
            <button
              key={p.id}
              type="button"
              className={periodo === p.id ? 'is-active' : ''}
              aria-pressed={periodo === p.id}
              onClick={() => setPeriodo(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {erro && !dados ? (
        <div className="dash-card dash-error"><i className="pi pi-exclamation-triangle" /> {erro}</div>
      ) : null}

      {dados ? (
        <>
          <section className="dash-kpis">
            <button type="button" className="dash-kpi" onClick={() => navigate('/consultaestampariapedido')}>
              <span className="dash-kpi-label"><i className="pi pi-shopping-cart" />Pedidos</span>
              <strong>{moedaCurta(dados.pedidosValor)}</strong>
              <span className="dash-kpi-sub">{numero(dados.pedidosQuantidade)} pedidos · ticket médio {moeda(resumo.ticketMedio)}</span>
            </button>
            <button type="button" className="dash-kpi" onClick={() => navigate('/consultaestampariaorcamento')}>
              <span className="dash-kpi-label"><i className="pi pi-file" />Orçamentos</span>
              <strong>{numero(dados.orcamentosQuantidade)}</strong>
              <span className="dash-kpi-sub">
                {moedaCurta(dados.orcamentosValor)}
                {resumo.conversao !== null ? ` · ${resumo.conversao}% viraram pedido` : ''}
              </span>
            </button>
            <button type="button" className="dash-kpi" onClick={() => navigate('/consultaestampariarecebimentopedido')}>
              <span className="dash-kpi-label"><i className="pi pi-wallet" />A receber</span>
              <strong>{moedaCurta(dados.receberValor)}</strong>
              <span className="dash-kpi-sub">{numero(dados.receberQuantidade)} pedidos com saldo · recebido no período {moedaCurta(dados.pedidosValorPago)}</span>
            </button>
            <button type="button" className={`dash-kpi ${dados.atrasadosQuantidade > 0 ? 'is-alert' : ''}`} onClick={() => navigate('/consultaestampariapedido')}>
              <span className="dash-kpi-label">
                <i className={`pi ${dados.atrasadosQuantidade > 0 ? 'pi-exclamation-triangle' : 'pi-calendar'}`} />
                Entregas atrasadas
              </span>
              <strong>{numero(dados.atrasadosQuantidade)}</strong>
              <span className="dash-kpi-sub">{numero(dados.entregasProximasQuantidade)} previstas nos próximos 7 dias</span>
            </button>
            <button type="button" className="dash-kpi" onClick={() => navigate('/consultaestampariaordemproducao')}>
              <span className="dash-kpi-label"><i className="pi pi-cog" />Em produção</span>
              <strong>{numero(dados.ordensEmProducao)}</strong>
              <span className="dash-kpi-sub">ordens abertas (últimos 90 dias) · {numero(dados.ordensFinalizadasPeriodo)} finalizadas no período</span>
            </button>
          </section>

          <div className="dash-grid">
            <section className="dash-card dash-chart-card">
              <header className="dash-card-header">
                <h2><i className="pi pi-chart-bar" />Valor dos pedidos por {serie.agruparMes ? 'mês' : 'dia'}</h2>
              </header>
              <div className="dash-chart">
                {serie.pontos.some(p => p.valor > 0) ? (
                  <Bar data={grafico.data} options={grafico.options} />
                ) : (
                  <p className="dash-empty">Nenhum pedido no período.</p>
                )}
              </div>
            </section>

            <section className="dash-card">
              <header className="dash-card-header">
                <h2><i className="pi pi-cog" />Produção por etapa</h2>
                <button type="button" className="dash-link" onClick={() => navigate('/consultaestampariaordemproducao')}>Ver ordens</button>
              </header>
              {etapas.length === 0 || Number(dados.ordensEmProducao) === 0 ? (
                <p className="dash-empty">Nenhuma ordem de produção em andamento.</p>
              ) : (
                <ul className="dash-bars">
                  {etapas.map(e => (
                    <li key={e.chave} title={`${ETAPA_OP[e.chave] || e.chave}: ${numero(e.quantidade)}`}>
                      <span className="dash-bar-label">{ETAPA_OP[e.chave] || e.chave}</span>
                      <span className="dash-bar-track">
                        <span className="dash-bar-fill" style={{ width: `${(Number(e.quantidade) / maiorEtapa) * 100}%` }} />
                      </span>
                      <span className="dash-bar-value">{numero(e.quantidade)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="dash-card">
              <header className="dash-card-header">
                <h2><i className="pi pi-list" />Pedidos por situação</h2>
              </header>
              {totalSituacoes === 0 ? (
                <p className="dash-empty">Nenhum pedido no período.</p>
              ) : (
                <ul className="dash-status">
                  {['ABERTO', 'PENDENTE', 'FECHADO', 'CANCELADO']
                    .map(chave => (dados.pedidosPorSituacao || []).find(i => i.chave === chave) || { chave, quantidade: 0 })
                    .map(s => {
                    const info = SITUACAO_PEDIDO[s.chave] || { label: s.chave, icon: 'pi-circle', classe: '' };
                    const pct = totalSituacoes > 0 ? Math.round((Number(s.quantidade) / totalSituacoes) * 100) : 0;
                    return (
                      <li key={s.chave} className={info.classe}>
                        <i className={`pi ${info.icon}`} />
                        <span>{info.label}</span>
                        <strong>{numero(s.quantidade)}</strong>
                        <em>{pct}%</em>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>


            <ListaPedidos
              titulo="Entregas atrasadas"
              icone="pi-exclamation-triangle"
              itens={dados.pedidosAtrasados || []}
              vazio="Nenhuma entrega atrasada."
              atraso
            />
            <ListaPedidos
              titulo="Próximas entregas (7 dias)"
              icone="pi-calendar"
              itens={dados.entregasProximas || []}
              vazio="Nenhuma entrega prevista para os próximos 7 dias."
              atraso={false}
            />
            <section className="dash-card">
              <header className="dash-card-header">
                <h2><i className="pi pi-users" />Pedidos por vendedor</h2>
              </header>
              {vendedores.length === 0 ? (
                <p className="dash-empty">Nenhum pedido no período.</p>
              ) : (
                <ul className="dash-bars is-money">
                  {vendedores.map(v => (
                    <li key={v.nome} title={`${v.nome}: ${moeda(v.valor)} em ${numero(v.quantidade)} pedidos`}>
                      <span className="dash-bar-label">{v.nome}</span>
                      <span className="dash-bar-track">
                        <span className="dash-bar-fill" style={{ width: `${(Number(v.valor) / maiorVendedor) * 100}%` }} />
                      </span>
                      <span className="dash-bar-value">{moedaCurta(v.valor)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      ) : null}

      {!dados && carregando ? (
        <div className="dash-kpis is-skeleton">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="dash-kpi" />)}
        </div>
      ) : null}
    </div>
  );
}
